import { create } from "zustand"
import { persist } from "zustand/middleware"
import type { User, Session } from "@/lib/api"
import { setSentryUser, clearSentryUser } from "@/lib/sentry"

interface AuthState {
  user: User | null
  session: Session | null
  isAuthenticated: boolean

  // Actions
  setAuth: (user: User, session: Session) => void
  clearAuth: () => void
  updateUser: (user: Partial<User>) => void
  getAccessToken: () => string | null
  getRefreshToken: () => string | null
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      isAuthenticated: false,

      setAuth: (user, session) => {
        set({
          user,
          session,
          isAuthenticated: true,
        })
        setSentryUser(user.id, user.email)
      },

      clearAuth: () => {
        set({
          user: null,
          session: null,
          isAuthenticated: false,
        })
        clearSentryUser()
      },

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      getAccessToken: () => {
        const session = get().session
        if (!session?.access_token) return null
        // Check if token is expired
        if (session.expires_at) {
          const expiresAt = typeof session.expires_at === 'number'
            ? session.expires_at * 1000 // Unix seconds → ms
            : Date.parse(session.expires_at)
          if (Date.now() > expiresAt) {
            // Token expired — clear auth silently
            get().clearAuth()
            return null
          }
        }
        return session.access_token
      },

      getRefreshToken: () => {
        const session = get().session
        return session?.refresh_token ?? null
      },
    }),
    {
      name: "ezer-auth-store",
      partialize: (state) => ({
        user: state.user,
        session: state.session,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
